/**
 * B-31 / B-32 — iMessage-style keyboard behavior for the chat composer.
 *
 * Existing keyboard handling, investigated 2026-07-19: the shell installs NO
 * @capacitor/keyboard plugin (package.json), so the iOS keyboard is raw
 * WKWebView behavior — it OVERLAYS the layout viewport (100dvh does not
 * shrink) and WKWebView reveals the focused field by PANNING the document
 * (a native scroll; the same mechanism behind the B-15 horizontal-pan
 * family). Two founder-reported consequences in chat:
 *
 *   B-31 — the fixed BottomTab stayed visible above the keyboard, wasting a
 *   64px chrome band exactly where conversation should be.
 *   B-32 — after keyboard dismissal WKWebView is known to leave the reveal
 *   pan un-restored, so the chat header ended up clipped under the status
 *   bar (vertical twin of resetHorizontalScroll's field evidence).
 *
 * Ratified behavior: composer focus (touch only) → tab bar hides, composer
 * rides the keyboard; blur → tab bar returns and the viewport pan is
 * restored. Desktop is untouched — every entry point below is gated on a
 * coarse pointer.
 *
 * Mechanism: the Composer toggles `html.chat-kb-open` on focus/blur;
 * BottomTab.module.css hides the bar under that class via :global. The
 * class lives on <html> (the `html.native-app` precedent, global.css) so a
 * hashed CSS module in another component tree can key on it without any
 * cross-component state plumbing.
 */

import { isNativeApp } from './platform';

/** Class set on <html> while the chat composer is focused on touch. */
export const CHAT_KB_OPEN_CLASS = 'chat-kb-open';

/**
 * Touch-context check — the "keyboard is an overlay" signal that gates the
 * whole iMessage-keyboard family (Composer focus/blur, Thread's viewport
 * listener). A desktop window narrowed to 800px has a hardware keyboard and
 * must keep its chrome, so we cannot gate on width alone.
 *
 * ROUND-2 ROOT CAUSE (B-31 device failure, founder on-device pass): the first
 * cut gated solely on `matchMedia('(pointer: coarse)')`. Everything downstream
 * of that gate was verified correct — the BottomTab hide rule compiles to
 * `html.chat-kb-open ._tab_<hash>{display:none}` (a real ancestor selector,
 * winning specificity) and the Composer wiring sets the class on focus — yet
 * on the real WKWebView the tab bar stayed visible. The single failing link is
 * this media query: a Capacitor WKWebView does not reliably report
 * `(pointer: coarse)` (WebKit has long-standing inconsistencies for pointer/
 * hover media features inside embedded web views; iPadOS "desktop-class"
 * browsing can even report `fine`), so the gate returned false and the class
 * was never set. jsdom's matchMedia polyfill returned false too, which is why
 * the source-stubbed tests passed while the device failed.
 *
 * Fix: trust every independent touch signal, not one media query.
 *   1. The Capacitor shell (`isNativeApp()`) is ALWAYS a touch device — the
 *      most authoritative signal on the exact surface B-31 failed on.
 *   2. `matchMedia('(pointer: coarse)')` — the original signal, kept for web
 *      mobile (Mobile Safari reports it correctly).
 *   3. `navigator.maxTouchPoints > 0` / `'ontouchstart' in window` — robust
 *      fallbacks for any WKWebView where the media query is unreliable.
 * Desktop (no native shell, fine pointer, no touch points) still returns
 * false, so its chrome is untouched — the two existing desktop pins hold.
 */
export function isCoarsePointer(): boolean {
  // Native shell = touch device, full stop. This alone fixes the B-31 device
  // failure regardless of how WKWebView answers the media query.
  if (isNativeApp()) return true;
  if (typeof window === 'undefined') return false;
  try {
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches
    ) {
      return true;
    }
  } catch {
    // fall through to the touch-point fallback
  }
  // `navigator.maxTouchPoints > 0` is the reliable touch signal where the
  // pointer media query is not: iOS WKWebView reports 5, desktop reports 0.
  // (Deliberately NOT `'ontouchstart' in window` — jsdom and some desktop
  // browsers define that property even without a touchscreen, which would
  // wrongly hide the tab bar on desktop.)
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  if (nav && typeof nav.maxTouchPoints === 'number' && nav.maxTouchPoints > 0) return true;
  return false;
}

/** Toggle the composer-focused marker class on the document element. */
export function setChatKeyboardOpen(open: boolean): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle(CHAT_KB_OPEN_CLASS, open);
}

/**
 * B-32 — vertical twin of resetHorizontalScroll (B-15): clear the document-
 * level pan WKWebView applied to reveal the focused composer. Runs only at
 * known boundaries (composer blur; keyboard-inset-gone viewport resize on
 * the chat thread) where any vertical document offset is provably stale —
 * the chat route is an exact viewport-height app frame (chat.module.css)
 * with its own inner scroller, so the DOCUMENT never legitimately scrolls
 * vertically there. Horizontal position is deliberately preserved (B-2/B-15
 * own that axis).
 */
export function resetVerticalViewportPan(): void {
  if (typeof document === 'undefined') return;
  const scroller = document.scrollingElement ?? document.documentElement;
  if (scroller && scroller.scrollTop !== 0) scroller.scrollTop = 0;
  // Some WebKit states scroll body instead of the documentElement.
  if (document.body && document.body.scrollTop !== 0) document.body.scrollTop = 0;
  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
    try {
      window.scrollTo({ left: window.scrollX ?? 0, top: 0 });
    } catch {
      // Older WebKit without options-object support.
      window.scrollTo(window.scrollX ?? 0, 0);
    }
  }
}

/**
 * Pure predicate for visualViewport resize events: true once the visual
 * viewport height is back within `tolerancePx` of the layout viewport —
 * i.e. the keyboard inset is gone (an open iOS keyboard is a ~300px delta;
 * URL-bar/rotation deltas track innerHeight and stay under the tolerance).
 */
export function keyboardInsetGone(
  visualViewportHeight: number,
  windowInnerHeight: number,
  tolerancePx = 50,
): boolean {
  return windowInnerHeight - visualViewportHeight < tolerancePx;
}

/**
 * B-37 — pure predicate for "the visual viewport has SETTLED": true once the
 * current visualViewport height is within `tolerancePx` of the PREVIOUS resize
 * event's height, i.e. two consecutive events agree the inset has stopped
 * moving. Used to hold the B-32 pan reset until the keyboard animation is done.
 *
 * Why: iOS attaches a form-accessory bar (the up/down/Done strip) above the
 * keyboard, anchored to the focused field. If we reset the document scroll
 * (resetVerticalViewportPan) WHILE the keyboard is still animating — which the
 * visualViewport 'resize' stream reports frame-by-frame — WKWebView can strand
 * that accessory bar mid-screen over the thread (founder capture). Gating the
 * reset on a settled inset keeps the reset off the animation frames.
 *
 * `prevHeight === null` (the first event after mount) is treated as NOT stable
 * so a reset never fires before we have a second data point.
 */
export function viewportInsetStable(
  prevHeight: number | null,
  currHeight: number,
  tolerancePx = 6,
): boolean {
  if (prevHeight === null) return false;
  return Math.abs(currHeight - prevHeight) <= tolerancePx;
}
